import React, { useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getWeddingHallSlots } from "@src/api/weddingHall/weddingHall";
import { useThrottle } from "@src/utils/hooks/useThrottle";
import { SortOptionDefaultType, SortOptionType } from "@src/types/weddingHall";
import { TFilterState, TFilterTag } from "@src/types/weddingHallFilter";
import NoData from "@src/components/common/noData/NoData";
import SearchFilter from "./SearchFilter";
import SlotSortOptionsBottomSheet from "../filter/SlotSortOptionsBottomSheet";
import FilterSheet, { useWeddingHallFilterSheet } from "../filter/FilterSheet";
import SelectDateBottomSheet from "../search/SelectDateBottomSheet";
import SlotCard from "../home/slot/SlotCard";
import Icon from "@src/components/common/icons/Icon";
import useScrollLock from "@src/utils/hooks/useEnableScrollLock";
import NoticeToast from "../NoticeToast";

const FETCH_SIZE = 30;
const DEFAULT_SORT_OPTION_NAME = "추천순"; //25.11 신규 추가
const sortOptions: { [key in SortOptionType | SortOptionDefaultType]?: string } = {
  RECOMMEND: DEFAULT_SORT_OPTION_NAME,
  FINAL_PRICE_ASC: "가격 낮은순",
  FINAL_PRICE_DESC: "가격 높은순",
  GUARANTEE_ASC: "예상하객수 낮은 순",
  GUARANTEE_DESC: "예상하객수 높은 순",
  WEDDING_TIME_ASC: "일정 빠른 순",
  WEDDING_TIME_DESC: "일정 늦은 순",
};

const ProductTab = () => {
  const location = useLocation();
  const throttle = useThrottle();
  const params = useParams();
  const partnerUuid = params.uuid;
  const toastTxt = location?.state?.toast;

  const { enableScrollLock, disableScrollLock } = useScrollLock();

  const [isLoading, setIsLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [showOrderSheet, setShowOrderSheet] = useState(false);
  const [showDateSheet, setShowDateSheet] = useState(false);

  const [slotCount, setSlotCount] = useState<number | null>(null);
  const [fetchCnt, setFetchCnt] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [slots, setSlots] = useState([]);
  const [sortType, setSortType] = useState<SortOptionType>(
    Object.keys(sortOptions)[0] as SortOptionType,
  ); //슬롯 정렬 기준
  const [tempMonths, setTempMonths] = useState<string[]>([]);
  const [tempDates, setTempDates] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  }); //예식일
  const [isResetMonthsAndDate, setIsResetMonthsAndDate] = useState(false);
  const { filterItems, currentFilterType, setCurrentFilterType, filterTags } =
    useWeddingHallFilterSheet({
      WEDDING_DAY: "dayOfWeeks",
      WEDDING_TIME: "weddingTimes",
      GUARANTEE: ["minGuarantee", "maxGuarantee"],
    });

  const [filterChips, setFilterChips] = useState<TFilterState[]>([
    { name: sortOptions[sortType], typeName: "SORT_OPTION", value: null },
    { name: "예식일", typeName: "WEDDING_DATE", value: null },
    { name: "요일", typeName: "WEDDING_DAY", value: null },
    { name: "시간", typeName: "WEDDING_TIME", value: null },
    { name: "예상하객수", typeName: "GUARANTEE", value: null },
  ]);
  const [tags, setTags] = useState<TFilterTag[]>([]);
  const [prevTags, setPrevTags] = useState<TFilterTag[]>([]); // 직전에 설정한 태그 상태 저장

  const fetchData = () => {
    setFetching(true);
    getWeddingHallSlots(
      partnerUuid,
      tempDates,
      tempMonths,
      tags,
      FETCH_SIZE,
      pageRef.current,
      sortType,
    )
      .then((res) => {
        if (pageRef.current > 1) {
          setSlots([...slots, ...res.item]);
        } else {
          setSlots(res.item);
          pageRef.current = 1;
          setPage(1);
          window.scrollTo({ top: 0, behavior: "smooth" }); // scroll to top
        }
        setFetchCnt(res.item?.length || 0); //30개 미만인지 체크
        setSlotCount(res.total); // 검색된 슬롯 갯수
      })
      .finally(() => {
        setIsLoading(false);
        setFetching(false);
      });
  };

  const onScroll = throttle(() => {
    const scrollHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const clientHeight = document.documentElement.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight * 0.6 && !fetching && fetchCnt === FETCH_SIZE) {
      pageRef.current = page + 1;
      setPage(page + 1);
    }
  }, 200);

  // 무한스크롤 상품 조회
  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [fetching]);

  useEffect(() => {
    if (page !== 1) {
      fetchData();
    }
  }, [page]);

  // 조건 선택 시 마다 상품 조회
  useEffect(() => {
    pageRef.current = 1;
    setPage(1);
    fetchData();
  }, [tempDates, tempMonths, tags, sortType]);

  const clickFilterChip = (filter) => {
    if (filter.name === "예식 빠른 순") {
      setShowOrderSheet(true);
    } else if (filter.name === "예식일") {
      setShowDateSheet(true);
    } else {
      setCurrentFilterType(filter.typeName);
      setPrevTags(tags);
    }
  };

  const clearFilter = () => {
    setTags([]);
    setTempMonths([]);
    setTempDates({ startDate: "", endDate: "" });
    setFilterChips((prevChips) => {
      return prevChips.map((chip) => {
        chip.value = null;
        return chip;
      });
    });
  };

  return (
    <div className="tab-product-wrap">
      {isLoading === false && (
        <>
          <div className="filter-wrap sticky">
            {filterChips.some((chip) => chip.value) && (
              <div
                className="badge badge-circle badge-gray-line cursor-pointer"
                style={{ padding: "4px", marginRight: "8px" }}
                onClick={clearFilter}
              >
                <Icon name="ReStart" size={18} />
              </div>
            )}
            {filterChips &&
              filterChips
                .slice(1, 5)
                .map((filter, idx) => (
                  <SearchFilter
                    key={`filter-chip-${idx}`}
                    filter={filter}
                    onClick={() => clickFilterChip(filter)}
                    currentFilterType={currentFilterType}
                  />
                ))}
          </div>
          <div className="filter-bottom-wrap">
            <span className="list-total-count">총 {slotCount}개</span>
            <div
              onClick={() => {
                setShowOrderSheet(true);
                enableScrollLock();
              }}
            >
              <span style={{ color: "#505050" }}>
                {filterChips[0].value || DEFAULT_SORT_OPTION_NAME}
              </span>
              <Icon name="ChevronDown" size={18} />
            </div>
          </div>

          <div className="slot-banner-container">
            <i className="icon icon-realtime-red500-18" />
            <div>
              <p className="bold">방문없이 바로 잔여타임을 선점하세요.</p>
              <p>
                <span>원하는 일정이 없다면? </span>
                <span className="bold" onClick={() => (window.location.href = "h2mwbell://chat")}>
                  1:1문의하기
                </span>
              </p>
            </div>
          </div>

          {slots && slots.length === 0 ? (
            <NoData
              title={"현재 공개된 잔여타임이 없습니다."}
              desc={`웨딩홀 전문가에게 문의해 주시면
원하는 일정의 잔여타임을 확인해 드릴게요.`}
              style={{ display: `${slots.length === 0 ? "" : "none"}` }}
            />
          ) : (
            <div className="product-card-list-wrap">
              {slots &&
                slots.map((slot) => (
                  <SlotCard key={slot.weddingDayId} slotInfo={slot} useLikeToast={true} />
                ))}
            </div>
          )}
        </>
      )}
      <SlotSortOptionsBottomSheet
        sortOptions={sortOptions}
        isShow={showOrderSheet}
        closeSheet={() => {
          setShowOrderSheet(false);
          disableScrollLock();
        }}
        changeOption={(val) => setSortType(val)}
        sortType={sortType}
        setFilterChips={setFilterChips}
      />
      <SelectDateBottomSheet
        tempMonths={tempMonths}
        setTempMonths={setTempMonths}
        tempDates={tempDates}
        setTempDates={setTempDates}
        isShow={showDateSheet}
        setIsShow={setShowDateSheet}
        setIsResetMonthAndDateData={setIsResetMonthsAndDate}
        setFilterChips={setFilterChips}
      />
      <FilterSheet
        isShow={currentFilterType !== null}
        items={filterItems}
        currentType={currentFilterType}
        setCurrentType={(type) => setCurrentFilterType(type)}
        tags={tags}
        prevTags={prevTags}
        setTags={setTags}
        onClose={() => {
          setCurrentFilterType(null);
        }}
        slotcnt={slotCount}
        setFilterChips={setFilterChips}
      />
      {toastTxt !== undefined && <NoticeToast hasMark={false} notice={toastTxt} btnName="닫기" />}
    </div>
  );
};

export default ProductTab;
